import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/client";
import LoadingScreen from "../components/LoadingScreen";
import Card from "../components/Card";
import TabBar from "../components/TabBar";
import Button from "../components/Button";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import imgTopCapturasEjemplares from "../assets/graphs/top_capturas_ejemplares.png";
import imgCapturasEspecies from "../assets/graphs/capturas_especies.png";
import imgCotosGestionados from "../assets/graphs/cotos_gestionados.png";
import imgTotalAutorizaciones from "../assets/graphs/total_autorizaciones.png";
import Dropdown from "../components/Dropdown";
import Modal from "../components/Modal";
import ChartSettings from "../components/ChartSettings";
import DownloadSettings from "../components/DownloadSettings";
import ShareSettings from "../components/ShareSettings";
import ShareEmailModal from "../components/ShareEmailModal";
import SpeciesSettings from "../components/SpeciesSettings.jsx";
import DateRangePicker from "../components/DateRangePicker";
import { loadFilterSection, saveFilterSection } from "../utils/filterStorage";
import { getCurrentSeasonDateRange } from "../utils/seasonDateRange";
import { formatNumber } from "../utils/formatNumber";
import "./styles.css";

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

// Importar imágenes de animales
import imgCorzo from "../assets/8b43135dbc0c9e16e1496fc50bb35c1676f2b40d.png";
import imgJabali from "../assets/35ee85eb15ac73963fb750c112a09261c36913b2.png";
import imgCabraMontes from "../assets/93300cd283de98250983c5cb1c9d7f0c35be5f26.png";
import imgCiervo from "../assets/8c8152eb2021b80f4880dee496903dade728006a.png";
import imgPalomaTorcaz from "../assets/d927dbbe0e85a2418fdc50ab89ee52120f52f9ce.png";
import imgMuflon from "../assets/d927dbbe0e85a2967834hdbaokcui37889ee52120f52f9ce.png";
import imgGamo from "../assets/d927dbbe0e85a2418fdc50ab89578120f52f9de.png";
import imgRebeco from "../assets/d927dbbe0e832k23b95n3lab89ee52120f52f9ce.png";
import imgTortola from "../assets/d927dbbe0e832349u8d95n3lab89ee52120f52f9ce.png";
import imgCodorniz from "../assets/img/especies/Codorniz.png";

// Importar iconos
import iconCalendar from "../assets/icons/calendar.svg";
import iconDownload from "../assets/icons/download.svg";
// import iconShare from '../assets/icons/share.svg'
import iconCog from "../assets/icons/settings.svg";
import iconChevronDown from "../assets/1393d640c763b6a2721534f3c4a2700a3659c21c.svg";
import iconTriangleUp from "../assets/icons/triangle-up.svg";
import iconChevronLeft from "../assets/icons/chevron-left-paginator.svg";
import iconChevronRight from "../assets/icons/chevron-right-paginator.svg";

export default function General() {
    const globalFilters = loadFilterSection("global") || {};
    const generalFilters = loadFilterSection("general") || {};
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [permissions, setPermissions] = useState(null);
    
    const [initialStackedBarData, setInitialStackedBarData] = useState([]);
    const [especies, setEspecies] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [cotos, setCotos] = useState([]);
    const [initialCapturasTotales, setInitialCapturasTotales] = useState([]);
    const [cotosData, setCotosDataInit] = useState({
        porcentaje_cotos_cyl: 84.53,
        porcentaje_otros_cotos: 15.47,
        total_cotos: 4739,
    });
    const [usersData, setUsersData] = useState({
        porcentaje_cazadores: 0,
        total_cazadores: 12,
        total_users: 1234,
    });
    const [distribucionRol, setDistribucionRol] = useState([]);

    // Cargar datos iniciales
    useEffect(() => {
        // Solo cargar datos cuando la autenticación esté completa
        if (!authLoading && user) {
            // Sentry.logger.info("User action", { userId: user.id || "unknown" });
            fetchInitialData();
        }
    }, [authLoading, user]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [especiesRes, provinciasRes, cotosRes, permissionsRes] = await Promise.all([
                api.get('/especies'),
                api.get('/provincias'),
                api.get('/cotos'),
                api.get('/auth/permissions')
            ]);

            // Transformar especies de {value, label} a {id, nombre}
            const especiesTransformadas = (especiesRes.data || []).map(especie => ({
                id: especie.value,
                nombre: especie.label
            }));

            setEspecies(especiesTransformadas);
            setProvincias((provinciasRes.data || [])
                .filter((p) => {
                    const label = typeof p === 'string' ? p : (p.label ?? p.nombre ?? '');
                    return !label.toLowerCase().includes('demo');
                })
                .map((p) => {
                    if (typeof p === 'string') return p.toUpperCase();
                    const label = p.label ?? p.nombre ?? '';
                    return { ...p, label: label.toUpperCase() };
                }));
            setCotos(cotosRes.data || []);
            setPermissions(permissionsRes.data?.permissions || null);
        } catch (err) {
            if (err.response?.status !== 401) {
                Sentry.captureException(new Error('Error loading initial data: ' + err.message));
            }
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const [activeTab, setActiveTab] = useState("general");
    const [selectedPeriod, setSelectedPeriod] = useState(
        generalFilters.selectedPeriod || "mensual",
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [chartType, setChartType] = useState("lineal");
    const [chartTypeSpecies, setChartTypeSpecies] = useState("barras");
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    //   const [isShareModalOpen, setIsShareModalOpen] = useState(false)
    const [isShareEmailModalOpen, setIsShareEmailModalOpen] = useState(false);
    const [fileType, setFileType] = useState("xls");
    const [shareType, setShareType] = useState("png");
    const [dateRange, setDateRange] = useState(
        globalFilters.dateRange || getCurrentSeasonDateRange(),
    );
    const [stackedBarData, setStackedBarData] = useState(initialStackedBarData);
    const [capturasTotales, setCapturasTotales] = useState(
        initialCapturasTotales,
    );
    const [selectedProvincia, setSelectedProvincia] = useState(
        globalFilters.selectedProvincia || [],
    );
    const [selectedCoto, setSelectedCoto] = useState(
        globalFilters.selectedCoto || [],
    );
    const [isLoadingChartData, setIsLoadingChartData] = useState(false);
    const [autorizacionesData, setAutorizacionesData] = useState({
        porcentaje_en_papel: 0,
        porcentaje_en_app: 12,
        total_en_papel: 12,
        total_en_app: 12,
        total_autorizaciones: 1234,
    });
    const [cotosDataState, setCotosDataState] = useState(cotosData);
    const chartScrollRef = useRef(null);
    const chartScrollSpeciesRef = useRef(null);
    const chartSettingsRef = useRef(null);
    const chartSettingsSpeciesRef = useRef(null);
    const [isSpeciesModalOpen, setIsSpeciesModalOpen] = useState(false);
    const [isSpeciesChartModalOpen, setIsSpeciesChartModalOpen] =
        useState(false);

    // Lista de especies que se seleccionaban por defecto
    // const DEFAULT_SELECTED_SPECIES = [
    //     "Corzo",
    //     "Jabalí",
    //     "Cabra montés",
    //     "Ciervo",
    //     "Gamo",
    // ];

    // Inicializar especies visibles basándose en las especies por defecto
    const [visibleSpecies, setVisibleSpecies] = useState(
        () => globalFilters.visibleSpecies || generalFilters.visibleSpecies || {},
    );

    useEffect(() => {
        saveFilterSection("global", {
            dateRange,
            selectedProvincia,
            selectedCoto,
            visibleSpecies,
        });
    }, [dateRange, selectedProvincia, selectedCoto, visibleSpecies]);

    useEffect(() => {
        saveFilterSection("general", {
            selectedPeriod,
        });
    }, [selectedPeriod]);

    // Efecto para actualizar especies visibles cuando las especies se cargan
    useEffect(() => {
        if (!especies || especies.length === 0) return;

        if (Object.keys(visibleSpecies).length === 0) {
            const initial = {};
            especies.forEach((especie) => {
                initial[especie.id] = true;
            });
            setVisibleSpecies(initial);
            return;
        }

        const missing = especies.filter(
            (especie) => !Object.prototype.hasOwnProperty.call(visibleSpecies, especie.id),
        );
        if (missing.length > 0) {
            const next = { ...visibleSpecies };
            missing.forEach((especie) => { next[especie.id] = true; });
            setVisibleSpecies(next);
        }
    }, [especies, visibleSpecies]);

    const speciesSettingsRef = useRef(null);
    const shareEmailModalRef = useRef(null);
    const pageContentRef = useRef(null);

    // Función para descargar en Excel
    const downloadExcel = async () => {
        try {
            // Import dinámico para mejor rendimiento
            const ExcelJS = await import("exceljs");
            // Crear workbook
            const workbook = new ExcelJS.default.Workbook();

            // Hoja 1: Resumen General
            const wsResumen = workbook.addWorksheet("Resumen");
            wsResumen.addRow(["Panel de Datos - Resumen General"]);
            wsResumen.addRow(["Fecha de generación:", new Date().toLocaleDateString("es-ES")]);
            wsResumen.addRow([
                "Rango de fechas:",
                `${formatDateForPicker(dateRange.startDate)} - ${formatDateForPicker(dateRange.endDate)}`,
            ]);
            wsResumen.addRow([]);
            wsResumen.addRow(["Métricas Principales"]);
            wsResumen.addRow([
                "Total Capturas",
                capturasTotales?.reduce(
                    (total, especie) =>
                        total + (parseInt(especie.total_ejemplares) || 0),
                    0,
                ) || 0,
            ]);

            if (!(user?.rol === "gestor" || user?.rol === "titular")) {
                wsResumen.addRow(["Total Cotos Gestionados", cotosDataState.total_cotos]);
                wsResumen.addRow(["Total Cotos CYL", cotosDataState.cotos_cyl]);
                wsResumen.addRow(["Total Otros Cotos", cotosDataState.otros_cotos]);
                wsResumen.addRow(["Cotos CYL (%)", cotosDataState.porcentaje_cotos_cyl]);
                wsResumen.addRow(["Otros Cotos (%)", cotosDataState.porcentaje_otros_cotos]);
            }
            wsResumen.addRow(["Total Usuarios", usersData.total_users]);
            wsResumen.addRow(["Usuarios Cazadores", usersData.total_cazadores]);
            wsResumen.addRow(["Porcentaje Cazadores (%)", usersData.porcentaje_cazadores]);
            wsResumen.addRow(["Total Autorizaciones", autorizacionesData.total_autorizaciones]);
            wsResumen.addRow(["Autorizaciones en CapturCYL (%)", autorizacionesData.porcentaje_en_app]);
            wsResumen.addRow([
                "Autorizaciones en Papel (%)",
                autorizacionesData.porcentaje_en_papel,
            ]);

            // Hoja 2: Capturas por Especie
            const wsCapturas = workbook.addWorksheet("Capturas por Especie");
            wsCapturas.addRow(["Especie", "Total Ejemplares"]);
            capturasTotales
                ?.filter((especie) => visibleSpecies[especie.especie_id])
                .forEach((especie) => {
                    wsCapturas.addRow([
                        especie.nombre_especie,
                        parseInt(especie.total_ejemplares) || 0,
                    ]);
                });

            // Hoja 3: Datos del Gráfico (por período)
            if (stackedBarData && stackedBarData.length > 0) {
                const especiesVisibles = especies.filter((e) => visibleSpecies[e.id]);
                const wsChart = workbook.addWorksheet("Datos por Período");
                
                const chartHeader = [
                    "Período",
                    ...especiesVisibles.map((e) => e.nombre),
                    "Total",
                ];
                wsChart.addRow(chartHeader);
                
                stackedBarData.forEach((item) => {
                    const row = [item.name];
                    let total = 0;
                    especiesVisibles.forEach((especie) => {
                        const value = parseInt(item[especie.id]) || 0;
                        row.push(value);
                        total += value;
                    });
                    row.push(total);
                    wsChart.addRow(row);
                });
            }

            // Descargar el archivo
            const fileName = `panel_datos_${new Date().toISOString().split("T")[0]}.xlsx`;
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error exportando Excel: " + error.message));
            }
            alert("Error al generar el archivo Excel. Por favor, intente de nuevo.");
            console.error("Error en downloadExcel:", error);
        }
    };

    // Función para convertir imagen a base64
    const imageToBase64 = (img) => {
        return new Promise((resolve) => {
            if (!img.src || img.src.startsWith("data:")) {
                resolve(null);
                return;
            }

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const image = new Image();
            image.crossOrigin = "anonymous";

            image.onload = () => {
                canvas.width = image.naturalWidth || image.width;
                canvas.height = image.naturalHeight || image.height;
                ctx.drawImage(image, 0, 0);
                try {
                    const base64 = canvas.toDataURL("image/png");
                    resolve(base64);
                } catch (e) {
                    resolve(null);
                }
            };

            image.onerror = () => {
                resolve(null);
            };

            // Añadir timestamp para evitar caché
            const separator = img.src.includes("?") ? "&" : "?";
            image.src = img.src + separator + "t=" + Date.now();
        });
    };

    // Función para descargar en PDF
    const downloadPDF = async () => {
        if (!pageContentRef.current) return;

        // Cerrar el modal primero
        setIsDownloadModalOpen(false);

        // Esperar a que el modal se cierre completamente
        await new Promise((resolve) => setTimeout(resolve, 300));

        try {
            // Imports dinámicos para mejor rendimiento
            const [html2canvasModule, jsPDFModule] = await Promise.all([
                import("html2canvas"),
                import("jspdf"),
            ]);
            const html2canvas = html2canvasModule.default;
            const jsPDF = jsPDFModule.default;

            // Mostrar indicador de carga
            const loadingDiv = document.createElement("div");
            loadingDiv.id = "pdf-loading";
            loadingDiv.style.cssText =
                "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;";
            loadingDiv.innerHTML =
                '<div style="background: white; padding: 20px 40px; border-radius: 8px; font-size: 16px;">Generando PDF...</div>';
            document.body.appendChild(loadingDiv);

            // Convertir todas las imágenes a base64 antes de capturar
            const images = pageContentRef.current.querySelectorAll("img");
            const originalSrcs = new Map();

            for (const img of images) {
                if (img.src && !img.src.startsWith("data:")) {
                    originalSrcs.set(img, img.src);
                    const base64 = await imageToBase64(img);
                    if (base64) {
                        img.src = base64;
                    }
                }
            }

            // Esperar un momento para que las imágenes se actualicen en el DOM
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Capturar el contenido de la página
            const canvas = await html2canvas(pageContentRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: "#f5f5f5",
                imageTimeout: 0,
            });

            // Crear PDF
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
                orientation:
                    canvas.width > canvas.height ? "landscape" : "portrait",
                unit: "mm",
                format: "a4",
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;
            const imgY = 10;

            // // Agregar título
            // pdf.setFontSize(16);
            // pdf.text(
            //     "Panel de Datos - Métricas del Sistema",
            //     pdfWidth / 2,
            //     10,
            //     { align: "center" },
            // );

            // Calcular si necesitamos múltiples páginas
            const scaledHeight = imgHeight * ratio;
            const pageContentHeight = pdfHeight - 20; // Margen superior e inferior

            if (scaledHeight <= pageContentHeight) {
                // Cabe en una página
                pdf.addImage(
                    imgData,
                    "PNG",
                    imgX,
                    15,
                    imgWidth * ratio,
                    imgHeight * ratio,
                );
            } else {
                // Necesitamos múltiples páginas
                let remainingHeight = imgHeight;
                let sourceY = 0;
                let isFirstPage = true;

                while (remainingHeight > 0) {
                    if (!isFirstPage) {
                        pdf.addPage();
                    }

                    const pageHeight = pageContentHeight / ratio;
                    const sliceHeight = Math.min(pageHeight, remainingHeight);

                    // Crear canvas temporal para esta porción
                    const tempCanvas = document.createElement("canvas");
                    tempCanvas.width = imgWidth;
                    tempCanvas.height = sliceHeight;
                    const tempCtx = tempCanvas.getContext("2d");
                    tempCtx.drawImage(
                        canvas,
                        0,
                        sourceY,
                        imgWidth,
                        sliceHeight,
                        0,
                        0,
                        imgWidth,
                        sliceHeight,
                    );

                    const tempImgData = tempCanvas.toDataURL("image/png");
                    pdf.addImage(
                        tempImgData,
                        "PNG",
                        imgX,
                        isFirstPage ? 15 : 10,
                        imgWidth * ratio,
                        sliceHeight * ratio,
                    );

                    sourceY += sliceHeight;
                    remainingHeight -= sliceHeight;
                    isFirstPage = false;
                }
            }

            // Agregar fecha de generación en el pie
            pdf.setFontSize(10);
            pdf.text(
                `Generado el ${new Date().toLocaleDateString("es-ES")} - Rango: ${formatDateForPicker(dateRange.startDate)} a ${formatDateForPicker(dateRange.endDate)}`,
                pdfWidth / 2,
                pdfHeight - 5,
                { align: "center" },
            );

            // Descargar el PDF
            const fileName = `panel_datos_${new Date().toISOString().split("T")[0]}.pdf`;
            pdf.save(fileName);

            // Restaurar las imágenes originales
            for (const [img, originalSrc] of originalSrcs) {
                img.src = originalSrc;
            }

            // Remover indicador de carga
            document.body.removeChild(loadingDiv);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error generating PDF: " + error.message));
            }
            const loadingDiv = document.getElementById("pdf-loading");
            if (loadingDiv) {
                document.body.removeChild(loadingDiv);
            }
            alert("Error al generar el PDF. Por favor, intente de nuevo.");
        }
    };

    // Función para manejar la descarga según el tipo seleccionado
    const handleDownload = () => {
        if (fileType === "xls") {
            downloadExcel();
            setIsDownloadModalOpen(false);
        } else if (fileType === "pdf") {
            // El modal se cierra dentro de downloadPDF antes de capturar
            downloadPDF();
        }
    };

    // Función para obtener el icono correspondiente a cada animal
    const getAnimalIcon = (nombreEspecie) => {
        const iconMap = {
            Corzo: imgCorzo,
            Jabalí: imgJabali,
            "Cabra montés": imgCabraMontes,
            Ciervo: imgCiervo,
            "Paloma torcaz": imgPalomaTorcaz,
            "Muflón": imgMuflon,
            Rebeco: imgRebeco,
            Gamo: imgGamo,
            "Tórtola europea": imgTortola,
            Codorniz: imgCodorniz,
        };
        return iconMap[nombreEspecie] || imgCorzo; // Default icon
    };

    // Función para filtrar datos del gráfico según especies visibles
    const filterStackedBarData = (data) => {
        if (!data || data.length === 0) return [];

        return data.map((item) => {
            const filteredItem = { name: item.name };

            // Solo incluir especies que estén visibles
            Object.entries(item).forEach(([key, value]) => {
                if (key === "name") return;

                // El key es el ID de la especie
                const especieId = parseInt(key);
                if (visibleSpecies[especieId]) {
                    filteredItem[especieId] = value;
                }
            });

            return filteredItem;
        });
    };

    // Aplicar filtro a los datos del gráfico
    const filteredStackedBarData = filterStackedBarData(stackedBarData);

    // Función para cargar datos del gráfico desde el backend
    const loadChartData = async (startDate, endDate) => {
        if (!startDate || !endDate) return;

        setIsLoadingChartData(true);
        try {
            // Obtener IDs de especies seleccionadas
            const selectedSpeciesIds = Object.entries(visibleSpecies)
                .filter(([id, isVisible]) => isVisible)
                .map(([id]) => id);

            // console.log("Loading chart data with params:", {
            //     startDate,
            //     endDate,
            //     selectedSpeciesIds,
            //     period: selectedPeriod,
            // });

            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                period: selectedPeriod,
            });

            // Añadir especies seleccionadas a los parámetros
            selectedSpeciesIds.forEach((id) => {
                params.append("selected_species[]", id);
            });

            // Agregar filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            // console.log("API URLs:", {
            //     chartData: "/api/chart-data?" + params,
            //     capturasTotales: "/api/capturas-totales?" + params,
            // });

            // console.log("Full request parameters:", {
            //     period: selectedPeriod,
            //     startDate,
            //     endDate,
            //     selectedSpeciesIds,
            // });

            const [chartResponse, totalesResponse] = await Promise.all([
                api.get("/general/chart-data?" + params),
                api.get("/general/capturas-totales?" + params),
            ]);

            // console.log("API responses:", {
            //     chartData: chartResponse.data,
            //     totalesData: totalesResponse.data,
            // });

            // console.log("Received data:", {
            //     chartData: chartResponse.data.length,
            //     totalesData: totalesResponse.data.length,
            //     period: selectedPeriod,
            //     firstChartItem: chartResponse.data[0],
            //     sampleData: chartResponse.data.slice(0, 3),
            // });

            setStackedBarData(chartResponse.data);
            setCapturasTotales(totalesResponse.data);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error loading data: " + error.message));
            }
        } finally {
            setIsLoadingChartData(false);
        }
    };

    // Función para cargar métricas (autorizaciones y cotos)
    const loadMetricsData = async () => {
        try {
            const params = new URLSearchParams({
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
            });

            // Agregar filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const [autorizacionesResponse, cotosResponse] = await Promise.all([
                api.get("/general/autorizaciones?" + params),
                api.get("/general/cotos?" + params),
            ]);

            setAutorizacionesData(autorizacionesResponse.data);
            setCotosDataState(cotosResponse.data);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error loading metrics data: " + error.message));
            }
        }
    };

    // Función para cargar datos de usuarios
    const loadUsersData = async () => {
        try {
            const params = new URLSearchParams({
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
            });

            // Agregar filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const [usersResponse, distribucionRolResponse] = await Promise.all([
                api.get("/general/usuarios?" + params),
                api.get("/general/distribucion-rol?" + params),
            ]);

            setUsersData(usersResponse.data || usersData);
            setDistribucionRol(distribucionRolResponse.data || []);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error loading users data: " + error.message));
            }
        }
    };

    // Efecto para cargar datos cuando cambia el rango de fechas o especies visibles
    useEffect(() => {
        if (
            dateRange.startDate &&
            dateRange.endDate &&
            Object.keys(visibleSpecies).length > 0
        ) {
            loadChartData(dateRange.startDate, dateRange.endDate);
            loadMetricsData();
            loadUsersData();
        }
    }, [
        dateRange.startDate,
        dateRange.endDate,
        visibleSpecies,
        selectedPeriod,
        selectedProvincia,
        selectedCoto,
    ]);

    // Scroll ambos gráficos al final (datos más recientes) cuando termina la carga
    useEffect(() => {
        if (!isLoadingChartData && stackedBarData.length > 0) {
            const timer = setTimeout(() => {
                if (chartScrollRef.current) {
                    chartScrollRef.current.scrollLeft = chartScrollRef.current.scrollWidth;
                }
                if (chartScrollSpeciesRef.current) {
                    chartScrollSpeciesRef.current.scrollLeft = chartScrollSpeciesRef.current.scrollWidth;
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isLoadingChartData, stackedBarData]);

    const tabs = useMemo(() => {
        const allTabs = [
            { id: "general", label: "General" },
            { id: "usuarios", label: "Usuarios" },
            { id: "autorizaciones", label: "Autorizaciones" },
            { id: "cupos", label: "Cupos" },
            { id: "capturas", label: "Capturas" },
        ];
        // Temporalmente deshabilitado - mostrar siempre el tab de usuarios
        // Descomentar cuando el API devuelva correctamente canViewUsers
        
        if (!permissions?.canViewUsers) {
            return allTabs.filter((tab) => tab.id !== "usuarios");
        }
        
        return allTabs;
    }, [permissions?.canViewUsers]);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        if (tab === "usuarios") navigate("/usuarios");
        if (tab === "autorizaciones") navigate("/autorizaciones");
        if (tab === "cupos") navigate("/cupos");
        if (tab === "capturas") navigate("/capturas");
        if (tab === "general") navigate("/general");
    }, [navigate]);

    const periodOptions = useMemo(
        () => [
            { value: "mensual", label: "Mensual" },
            { value: "anual", label: "Anual" },
            { value: "semanal", label: "Semanal" },
        ],
        [],
    );

    // Función para convertir fecha ISO a formato DD/MM/YYYY
    const formatDateForPicker = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Función para generar datos del gráfico de totales basado en datos reales
    const chartData = useMemo(() => {
        if (!stackedBarData || stackedBarData.length === 0) {
            return [];
        }

        return stackedBarData.map((item) => {
            // Sumar todas las especies visibles para cada período
            let total = 0;
            Object.entries(item).forEach(([key, value]) => {
                if (key === "name") return;
                const especieId = parseInt(key);
                if (visibleSpecies[especieId] && value) {
                    total += parseInt(value) || 0;
                }
            });

            return {
                name: item.name,
                value: total,
            };
        });
    }, [stackedBarData, visibleSpecies]);

    // Configuración para gráfico de líneas (Chart.js)
    const lineChartData = useMemo(
        () => ({
            labels: chartData.map((item) => item.name),
            datasets: [
                {
                    label: "Total Ejemplares capturados",
                    data: chartData.map((item) => item.value),
                    borderColor: "#1C8153",
                    backgroundColor: "rgba(28, 129, 83, 0.2)",
                    tension: 0.1,
                },
            ],
        }),
        [chartData],
    );

    const lineChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                title: {
                    display: false,
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    // max: 10000,
                    ticks: {
                        stepSize: 1000,
                    },
                },
            },
        }),
        [],
    );

    // Configuración para gráfico de barras (Chart.js)
    const getSpeciesColors = useCallback(() => {
        const colors = [
            "#839998",
            "#CFC5B6",
            "#064340",
            "#1C8153",
            "#988A74",
            "#DFF0E7",
        ];
        return colors;
    }, []);

    const barChartData = useMemo(
        () => ({
            labels: filteredStackedBarData.map((item) => item.name),
            datasets: especies
                .filter((especie) => visibleSpecies[especie.id])
                .map((especie, index) => {
                    const colors = getSpeciesColors();
                    return {
                        label: especie.nombre,
                        data: filteredStackedBarData.map(
                            (item) => item[especie.id] || 0,
                        ),
                        backgroundColor: colors[index % colors.length],
                    };
                }),
        }),
        [filteredStackedBarData, especies, visibleSpecies, getSpeciesColors],
    );

    const barChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                    labels: {
                        boxWidth: 12,
                        padding: 8,
                        font: {
                            size: 11,
                        },
                    },
                },
                title: {
                    display: false,
                },
            },
            scales: {
                x: {
                    stacked: false,
                },
                y: {
                    stacked: false,
                    beginAtZero: true,
                    // max: 10000,
                    ticks: {
                        stepSize: 1000,
                    },
                },
            },
        }),
        [],
    );

    // Opciones específicas para gráfico de líneas de especies (con leyenda)
    const speciesLineChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                    labels: {
                        boxWidth: 12,
                        padding: 8,
                        font: {
                            size: 11,
                        },
                    },
                },
                title: {
                    display: false,
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        stepSize: 1000,
                    },
                },
            },
        }),
        [],
    );

    // Verificar autenticación primero
    if (authLoading) {
        return <LoadingScreen />;
    }

    if (loading) {
        return <LoadingScreen />;
    }

    if (error) {
        return (
            <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#721c24',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                margin: '20px'
            }}>
                <h2>Error al cargar los datos</h2>
                <p>{error}</p>
                <Button onClick={() => window.location.reload()}>
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <>
        <div className="page-general" ref={pageContentRef}>
            <h1 className="page-title">Panel de datos del Sistema de control telemático de capturas en los cotos de caza de Castilla y León</h1>

            <div className="page-tabs">
                <TabBar
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />
            </div>

            <div className="page-header-section">
                <div className="page-header-title">
                    <h2 className="page-section-title">
                        Métricas de uso del sistema telemático
                    </h2>
                    {/* <p className="page-section-subtitle">Datos desde el 1 de abril de 2025 · Actualizado a 24 de octubre de 2025</p> */}
                </div>
            </div>

            <div className="page-content">
                <div className="page-filters">
                    <div style={{display: 'flex', gap: '35px', flexWrap: 'wrap'}}>
                        <div className="filter-group">
                            <label className="filter-label">
                                Seleccione un rango de fechas
                            </label>
                            <DateRangePicker
                                startDate={formatDateForPicker(dateRange.startDate)}
                                endDate={formatDateForPicker(dateRange.endDate)}
                                onChange={setDateRange}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Provincia
                            </label>
                            <Dropdown
                                options={provincias}
                                value={selectedProvincia}
                                onChange={(value) => {
                                    setSelectedProvincia(value);
                                }}
                                placeholder="Todas las provincias"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Coto
                            </label>
                            <Dropdown
                                options={cotos}
                                value={selectedCoto}
                                onChange={(value) => {
                                    setSelectedCoto(value);
                                }}
                                placeholder="Todos los cotos"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Especies
                            </label>
                            <Button
                                variant="primary"
                                size="s"
                                style={{ position: 'relative' }}
                                onClick={() =>
                                    setIsSpeciesModalOpen(true)
                                }
                            >
                                {/* <img src={iconCog} alt="" /> */}
                                Selecciona las especies
                                {Object.values(visibleSpecies).filter(Boolean).length > 0 && (
                                    <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#2D6A4F', color: 'white', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', lineHeight: '1', pointerEvents: 'none' }}>
                                        {Object.values(visibleSpecies).filter(Boolean).length}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                    <div className="filter-actions">
                        <Button
                            variant="accent"
                            onClick={() => setIsDownloadModalOpen(true)}
                        >
                            <img
                                src={iconDownload}
                                alt=""
                                className="button-icon button-icon-white"
                            />
                            <span>Descargar</span>
                        </Button>
                        {/* <Button variant="primary" onClick={() => setIsShareModalOpen(true)}>
                            <img src={iconShare} alt="" />
                        </Button> */}
                    </div>
                </div>

                {/* KPIs principales */}
                <div className="panel-section-title">Resumen</div>
                <div className="panel-kpi-row">
                    <div className="panel-kpi-card">
                        <span className="kpi-label">Total ejemplares capturados</span>
                        <span className="kpi-value">
                            {formatNumber(
                                capturasTotales?.reduce(
                                    (total, especie) =>
                                        total + (parseInt(especie.total_ejemplares) || 0),
                                    0,
                                ),
                            )}
                        </span>
                        <span className="kpi-foot">Periodo seleccionado</span>
                    </div>
                    {!(user?.rol === "gestor" || user?.rol === "titular") && (
                        <div className="panel-kpi-card">
                            <span className="kpi-label">Total cotos gestionados</span>
                            <span className="kpi-value">
                                {formatNumber(cotosDataState.total_cotos)}
                            </span>
                            <span className="kpi-foot">
                                {cotosDataState.porcentaje_cotos_cyl
                                    ? Math.round(cotosDataState.porcentaje_cotos_cyl * 10) / 10 + "% del total"
                                    : ""}
                            </span>
                        </div>
                    )}
                    {permissions?.canViewUsers && (
                        <div className="panel-kpi-card">
                            <span className="kpi-label">Total usuarios</span>
                            <span className="kpi-value">
                                {formatNumber(usersData.total_users)}
                            </span>
                            <span className="kpi-foot">En el sistema</span>
                        </div>
                    )}
                    <div className="panel-kpi-card">
                        <span className="kpi-label">Total autorizaciones</span>
                        <span className="kpi-value">
                            {formatNumber(autorizacionesData.total_autorizaciones)}
                        </span>
                        <span className="kpi-foot">Periodo seleccionado</span>
                    </div>
                </div>

                {/* Especies registradas */}
                {capturasTotales && capturasTotales.length > 0 && (
                    <>
                        <div className="panel-section-title">Especies registradas</div>
                        <Card>
                            <div className="animals-grid">
                                {capturasTotales
                                    ?.filter(
                                        (especie) =>
                                            visibleSpecies[especie.especie_id],
                                    )
                                    .sort((a, b) =>
                                        a.nombre_especie.localeCompare(
                                            b.nombre_especie,
                                        ),
                                    )
                                    .map((especie) => (
                                        <div
                                            key={especie.especie_id}
                                            className="animal-item"
                                        >
                                            <img
                                                src={getAnimalIcon(
                                                    especie.nombre_especie,
                                                )}
                                                alt={especie.nombre_especie}
                                                className="animal-icon"
                                            />
                                            <div className="animal-info">
                                                <p className="animal-name">
                                                    {especie.nombre_especie}
                                                </p>
                                                <p className="animal-value">
                                                    {formatNumber(especie.total_ejemplares)}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </Card>
                    </>
                )}

                <div className="panel-charts-grid">
                    {capturasTotales && capturasTotales.length > 0 && (
                        <>
                            <Card>
                                <div className="card-section">
                                    <div className="card-section-header">
                                        <p className="card-section-title">
                                            Total capturas ejemplares
                                        </p>
                                        <div className="card-actions">
                                            {/* <Button variant="primary" size="s">
                            <img src={iconDownload} alt="" />
                            </Button> */}
                                            <Button
                                                variant="primary"
                                                size="s"
                                                onClick={() =>
                                                    setIsModalOpen(true)
                                                }
                                            >
                                                <img src={iconCog} alt="" />
                                            </Button>
                                            <Dropdown
                                                options={periodOptions}
                                                value={selectedPeriod}
                                                onChange={setSelectedPeriod}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            position: "relative",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            width: "100%",
                                            minWidth: 0,
                                        }}
                                    >
                                        {/* Botón izquierdo - fuera del gráfico */}
                                        {(selectedPeriod === "anual"
                                            ? chartData.length > 2
                                            : chartData.length > 5) && (
                                            <button
                                                onClick={() => {
                                                    if (
                                                        chartScrollRef.current
                                                    ) {
                                                        chartScrollRef.current.scrollBy(
                                                            {
                                                                left: -200,
                                                                behavior:
                                                                    "smooth",
                                                            },
                                                        );
                                                    }
                                                }}
                                                style={{
                                                    background: "#fff",
                                                    border: "1px solid var(--text-field-border-color)",
                                                    borderRadius: "50%",
                                                    width: "32px",
                                                    height: "32px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer",
                                                    flexShrink: 0,
                                                    boxShadow:
                                                        "0px 2px 8px rgba(0, 0, 0, 0.1)",
                                                }}
                                            >
                                                <img
                                                    src={iconChevronLeft}
                                                    alt="Anterior"
                                                    style={{
                                                        width: "12px",
                                                        height: "12px",
                                                    }}
                                                />
                                            </button>
                                        )}

                                        {/* Contenedor del gráfico sin eje Y manual */}
                                        <div
                                            style={{
                                                position: "relative",
                                                display: "flex",
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            {/* Área del gráfico con scroll */}
                                            <div
                                                ref={chartScrollRef}
                                                className="chart-container-large card-section-chart"
                                                style={{
                                                    height: "187px",
                                                    overflowX: "auto",
                                                    overflowY: "hidden",
                                                    position: "relative",
                                                    scrollBehavior: "smooth",
                                                    scrollbarWidth: "none", // Firefox
                                                    msOverflowStyle: "none", // IE/Edge
                                                    flex: 1,
                                                    minWidth: 0,
                                                    maxWidth: "100%",
                                                }}
                                            >
                                                <style>{`
                                .chart-container-large::-webkit-scrollbar {
                                display: none; /* Chrome, Safari, Opera */
                                }
                            `}</style>
                                                <div
                                                    style={{
                                                        minWidth:
                                                            selectedPeriod ===
                                                            "anual"
                                                                ? "100%"
                                                                : `${Math.max(800, chartData.length * 60)}px`,
                                                        width:
                                                            selectedPeriod ===
                                                            "anual"
                                                                ? "100%"
                                                                : "max-content",
                                                    }}
                                                >
                                                    {isLoadingChartData ? (
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                height: "187px",
                                                                color: "var(--dk-green-600)",
                                                            }}
                                                        >
                                                            Cargando datos...
                                                        </div>
                                                    ) : chartData.length > 0 ? (
                                                        chartType ===
                                                        "barras" ? (
                                                            <div
                                                                style={{
                                                                    height: "187px",
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <Bar
                                                                    data={{
                                                                        labels: chartData.map(
                                                                            (
                                                                                item,
                                                                            ) =>
                                                                                item.name,
                                                                        ),
                                                                        datasets:
                                                                            [
                                                                                {
                                                                                    label: "Total Capturas",
                                                                                    data: chartData.map(
                                                                                        (
                                                                                            item,
                                                                                        ) =>
                                                                                            item.value,
                                                                                    ),
                                                                                    backgroundColor:
                                                                                        "#1B6D48",
                                                                                    borderColor:
                                                                                        "#14532D",
                                                                                    borderWidth: 1,
                                                                                },
                                                                            ],
                                                                    }}
                                                                    options={
                                                                        lineChartOptions
                                                                    }
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    height: "187px",
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <Line
                                                                    data={
                                                                        lineChartData
                                                                    }
                                                                    options={
                                                                        lineChartOptions
                                                                    }
                                                                />
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                height: "187px",
                                                                color: "var(--neutral-500)",
                                                            }}
                                                        >
                                                            No hay datos
                                                            disponibles
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Botón derecho - fuera del gráfico */}
                                        {(selectedPeriod === "anual"
                                            ? chartData.length > 2
                                            : chartData.length > 5) && (
                                            <button
                                                onClick={() => {
                                                    if (
                                                        chartScrollRef.current
                                                    ) {
                                                        chartScrollRef.current.scrollBy(
                                                            {
                                                                left: 200,
                                                                behavior:
                                                                    "smooth",
                                                            },
                                                        );
                                                    }
                                                }}
                                                style={{
                                                    background: "#fff",
                                                    border: "1px solid var(--text-field-border-color)",
                                                    borderRadius: "50%",
                                                    width: "32px",
                                                    height: "32px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer",
                                                    flexShrink: 0,
                                                    boxShadow:
                                                        "0px 2px 8px rgba(0, 0, 0, 0.1)",
                                                }}
                                            >
                                                <img
                                                    src={iconChevronRight}
                                                    alt="Siguiente"
                                                    style={{
                                                        width: "12px",
                                                        height: "12px",
                                                    }}
                                                />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </Card>
                            <Card>
                                <div className="card-section">
                                    <div className="card-section-header">
                                        <p className="card-section-title">
                                            Capturas de ejemplares por especies
                                            {isLoadingChartData && (
                                                <span
                                                    style={{
                                                        marginLeft: "8px",
                                                        fontSize: "12px",
                                                        color: "var(--dk-green-600)",
                                                    }}
                                                >
                                                    (Actualizando...)
                                                </span>
                                            )}
                                        </p>
                                        <div className="card-actions">
                                            {/* <Button variant="primary" size="s">
                            <img src={iconDownload} alt="" />
                            </Button> */}
                                            <Button
                                                variant="primary"
                                                size="s"
                                                onClick={() =>
                                                    setIsSpeciesChartModalOpen(
                                                        true,
                                                    )
                                                }
                                            >
                                                <img src={iconCog} alt="" />
                                            </Button>
                                            <Dropdown
                                                options={periodOptions}
                                                value={selectedPeriod}
                                                onChange={setSelectedPeriod}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            position: "relative",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            width: "100%",
                                            minWidth: 0,
                                        }}
                                    >
                                        {/* Botón izquierdo - fuera del gráfico */}
                                        {(selectedPeriod === "anual"
                                            ? filteredStackedBarData.length > 2
                                            : filteredStackedBarData.length >
                                              5) && (
                                            <button
                                                onClick={() => {
                                                    if (
                                                        chartScrollSpeciesRef.current
                                                    ) {
                                                        chartScrollSpeciesRef.current.scrollBy(
                                                            {
                                                                left: -200,
                                                                behavior:
                                                                    "smooth",
                                                            },
                                                        );
                                                    }
                                                }}
                                                style={{
                                                    background: "#fff",
                                                    border: "1px solid var(--text-field-border-color)",
                                                    borderRadius: "50%",
                                                    width: "32px",
                                                    height: "32px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer",
                                                    flexShrink: 0,
                                                    boxShadow:
                                                        "0px 2px 8px rgba(0, 0, 0, 0.1)",
                                                }}
                                            >
                                                <img
                                                    src={iconChevronLeft}
                                                    alt="Anterior"
                                                    style={{
                                                        width: "12px",
                                                        height: "12px",
                                                    }}
                                                />
                                            </button>
                                        )}

                                        {/* Contenedor del gráfico sin eje Y manual */}
                                        <div
                                            style={{
                                                position: "relative",
                                                display: "flex",
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            {/* Área del gráfico con scroll */}
                                            <div
                                                ref={chartScrollSpeciesRef}
                                                className="chart-container-medium card-section-chart"
                                                style={{
                                                    height: "250px",
                                                    overflowX: "auto",
                                                    overflowY: "hidden",
                                                    position: "relative",
                                                    scrollBehavior: "smooth",
                                                    scrollbarWidth: "none", // Firefox
                                                    msOverflowStyle: "none", // IE/Edge
                                                    flex: 1,
                                                    minWidth: 0,
                                                    maxWidth: "100%",
                                                }}
                                            >
                                                <style>{`
                                .chart-container-medium::-webkit-scrollbar {
                                display: none; /* Chrome, Safari, Opera */
                                }
                            `}</style>
                                                <div
                                                    style={{
                                                        minWidth:
                                                            selectedPeriod ===
                                                            "anual"
                                                                ? "100%"
                                                                : `${Math.max(800, filteredStackedBarData.length * 60)}px`,
                                                        width:
                                                            selectedPeriod ===
                                                            "anual"
                                                                ? "100%"
                                                                : "max-content",
                                                    }}
                                                >
                                                    {isLoadingChartData ? (
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                height: "100%",
                                                                color: "var(--dk-green-600)",
                                                            }}
                                                        >
                                                            Cargando datos...
                                                        </div>
                                                    ) : filteredStackedBarData.length >
                                                      0 ? (
                                                        chartTypeSpecies ===
                                                        "lineal" ? (
                                                            <div
                                                                style={{
                                                                    height: "250px",
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <Line
                                                                    data={{
                                                                        labels: filteredStackedBarData.map(
                                                                            (
                                                                                item,
                                                                            ) =>
                                                                                item.name,
                                                                        ),
                                                                        datasets:
                                                                            especies
                                                                                .filter(
                                                                                    (
                                                                                        especie,
                                                                                    ) =>
                                                                                        visibleSpecies[
                                                                                            especie
                                                                                                .id
                                                                                        ],
                                                                                )
                                                                                .map(
                                                                                    (
                                                                                        especie,
                                                                                        index,
                                                                                    ) => {
                                                                                        const colors =
                                                                                            getSpeciesColors();
                                                                                        const borderColor =
                                                                                            colors[
                                                                                                index %
                                                                                                    colors.length
                                                                                            ].replace(
                                                                                                "0.8",
                                                                                                "1",
                                                                                            );
                                                                                        const backgroundColor =
                                                                                            colors[
                                                                                                index %
                                                                                                    colors.length
                                                                                            ].replace(
                                                                                                "0.8",
                                                                                                "0.2",
                                                                                            );

                                                                                        return {
                                                                                            label: especie.nombre,
                                                                                            data: filteredStackedBarData.map(
                                                                                                (
                                                                                                    item,
                                                                                                ) =>
                                                                                                    item[
                                                                                                        especie
                                                                                                            .id
                                                                                                    ] ||
                                                                                                    0,
                                                                                            ),
                                                                                            borderColor:
                                                                                                borderColor,
                                                                                            backgroundColor:
                                                                                                backgroundColor,
                                                                                            tension: 0.1,
                                                                                            fill: false,
                                                                                        };
                                                                                    },
                                                                                ),
                                                                    }}
                                                                    options={
                                                                        speciesLineChartOptions
                                                                    }
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    height: "250px",
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <Bar
                                                                    data={
                                                                        barChartData
                                                                    }
                                                                    options={
                                                                        barChartOptions
                                                                    }
                                                                />
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                height: "100%",
                                                                color: "var(--neutral-500)",
                                                            }}
                                                        >
                                                            No hay datos
                                                            disponibles
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Botón derecho - fuera del gráfico */}
                                        {(selectedPeriod === "anual"
                                            ? filteredStackedBarData.length > 2
                                            : filteredStackedBarData.length >
                                              5) && (
                                            <button
                                                onClick={() => {
                                                    if (
                                                        chartScrollSpeciesRef.current
                                                    ) {
                                                        chartScrollSpeciesRef.current.scrollBy(
                                                            {
                                                                left: 200,
                                                                behavior:
                                                                    "smooth",
                                                            },
                                                        );
                                                    }
                                                }}
                                                style={{
                                                    background: "#fff",
                                                    border: "1px solid var(--text-field-border-color)",
                                                    borderRadius: "50%",
                                                    width: "32px",
                                                    height: "32px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer",
                                                    flexShrink: 0,
                                                    boxShadow:
                                                        "0px 2px 8px rgba(0, 0, 0, 0.1)",
                                                }}
                                            >
                                                <img
                                                    src={iconChevronRight}
                                                    alt="Siguiente"
                                                    style={{
                                                        width: "12px",
                                                        height: "12px",
                                                    }}
                                                />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        </>
                    )}
                </div>

                <div className="page-grid">
                    {!(user?.rol === "gestor" || user?.rol === "titular") && (
                        <div className="page-grid-left">
                            <Card style={{ flex: "0 1 auto", height: "auto" }}>
                                <div className="card-content">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            Gestión de cotos
                                        </h3>
                                        <div className="card-value-large">
                                            {formatNumber(cotosDataState.total_cotos)}
                                        </div>
                                    </div>
                                    <div
                                        className="card-pie-chart"
                                        style={{
                                            height: "280px",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flex: "1",
                                            position: "relative",
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "240px",
                                                height: "240px",
                                                margin: "0 auto 20px auto",
                                                }}
                                            >
                                                <Doughnut
                                                    data={{
                                                        labels: [
                                                            "Cotos gestionados",
                                                            "Cotos no gestionados",
                                                        ],
                                                        datasets: [
                                                            {
                                                                data: [
                                                                    cotosDataState.porcentaje_cotos_cyl,
                                                                    cotosDataState.porcentaje_otros_cotos,
                                                                ],
                                                                backgroundColor: [
                                                                    "#2D6A4F", // Verde oscuro para Cotos CYL
                                                                    "#95D5B2", // Verde claro para Otros cotos
                                                                ],
                                                                borderWidth: 0,
                                                                cutout: "0%", // Sin agujero para forma de queso completo
                                                            },
                                                        ],
                                                    }}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: true,
                                                        plugins: {
                                                            legend: {
                                                                display: false,
                                                            },
                                                            tooltip: {
                                                                callbacks: {
                                                                    label: function (
                                                                        context,
                                                                    ) {
                                                                        return (
                                                                            context.label +
                                                                            ": " +
                                                                            context.parsed +
                                                                            "%"
                                                                        );
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                        </div>
                                        {/* Etiquetas personalizadas más grandes y centradas */}
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: 'column',
                                                gap: "12px",
                                                alignItems: "flex-start",
                                                fontSize: "13px",
                                                fontWeight: "500",
                                                width: "100%",
                                                paddingLeft: "20px",
                                                paddingRight: "20px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    width: "100%",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "12px",
                                                        height: "12px",
                                                        borderRadius: "50%",
                                                        backgroundColor: "#2D6A4F",
                                                        flexShrink: 0,
                                                    }}
                                                ></div>
                                                <span style={{ color: "#374151", flex: 1 }}>
                                                    Cotos gestionados en la
                                                    plataforma
                                                </span>
                                                <span
                                                    style={{
                                                        color: "#374151",
                                                        fontWeight: "600",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {formatNumber(cotosDataState.cotos_cyl)} (
                                                    {Math.round(
                                                        cotosDataState.porcentaje_cotos_cyl *
                                                            10,
                                                    ) / 10}
                                                    %)
                                                </span>
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    width: "100%",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "12px",
                                                        height: "12px",
                                                        borderRadius: "50%",
                                                        backgroundColor: "#95D5B2",
                                                        flexShrink: 0,
                                                    }}
                                                ></div>
                                                <span style={{ color: "#374151", flex: 1 }}>
                                                    Cotos no gestionados en la
                                                    plataforma
                                                </span>
                                                <span
                                                    style={{
                                                        color: "#374151",
                                                        fontWeight: "600",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {formatNumber(cotosDataState.otros_cotos)} (
                                                    {Math.round(
                                                        cotosDataState.porcentaje_otros_cotos *
                                                            10,
                                                    ) / 10}
                                                    %)
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    )}

                    <div className="page-grid-right">
                        {permissions?.canViewUsers && (
                            <Card style={{ flex: "0 1 auto", height: "auto" }}>
                                <div className="card-header-section">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            Total Usuarios*
                                        </h3>
                                        <div className="card-value-large">
                                            {formatNumber(
                                                usersData.total_users,
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className="card-subtitle"
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "4px",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        {distribucionRol.length > 0 ? (
                                            distribucionRol.map((item) => {
                                                //
                                                let hasFilters = selectedProvincia.length > 0 || selectedCoto.length > 0;
                                                if (hasFilters) {
                                                    if(item.name === "Cazador"){
                                                        return (
                                                            <div key={item.name}>
                                                                <span
                                                                    className="subtitle-value"
                                                                >
                                                                    {formatNumber(usersData.total_cazadores)}{" "} 
                                                                </span>
                                                                <span className="subtitle-text">
                                                                    usuarios {item.name}
                                                                </span>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }
                                                return (
                                                    <div key={item.name}>
                                                        <span
                                                            className="subtitle-value"
                                                        >
                                                            {formatNumber(item.total)}{" "} 
                                                        </span>
                                                        <span className="subtitle-text">
                                                            usuarios {item.name}
                                                        </span>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <span className="subtitle-text">
                                                No hay datos disponibles
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {/* <div className="card-content">
                                    <div className="progress-container">
                                        <div className="progress-bar">
                                            <div
                                                className="progress-fill"
                                                style={{
                                                    width: `${usersData.porcentaje_cazadores}%`,
                                                }}
                                            ></div>
                                        </div>
                                        <div className="progress-labels">
                                            <span>0%</span>
                                            <span>100%</span>
                                        </div>
                                    </div>
                                    <p className="card-note">
                                        *Gestores, titulares, cazadores y agentes de la autoridad
                                    </p>
                                </div> */}
                            </Card>
                        )}

                        {/* <Card style={{ flex: '0 1 auto', height: 'auto' }}>
              <div className="card-content">
                <div className="card-header">
                  <h3 className="card-title">Acceso a plataforma*</h3>
                </div>
                <div className="card-stats-row">
                  <div className="stat-item">
                    <p className="stat-label">Sesiones</p>
                    <p className="stat-value">75.094</p>
                  </div>
                  <div className="stat-item">
                    <p className="stat-label">Visitas</p>
                    <p className="stat-value">452.314</p>
                  </div>
                </div>
                <p className="card-note">* www.capturascotos.app</p>
              </div>
            </Card> */}
                        {autorizacionesData.total_autorizaciones > 0 && (
                            <Card style={{ flex: "0 1 auto", height: "auto" }}>
                                <div className="card-content">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            Total Autorizaciones
                                        </h3>
                                        <div className="card-value-large">
                                            {
                                                formatNumber(autorizacionesData.total_autorizaciones)
                                            }
                                        </div>
                                    </div>
                                    <div
                                        className="card-pie-chart"
                                        style={{
                                            height: "200px",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flex: "1",
                                            position: "relative",
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "240px",
                                                height: "240px",
                                                margin: "0 auto 16px auto",
                                            }}
                                        >
                                            <Doughnut
                                                data={{
                                                    labels: [
                                                        "CapturCYL",
                                                        "En papel",
                                                    ],
                                                    datasets: [
                                                        {
                                                            data: [
                                                                autorizacionesData.porcentaje_en_app,
                                                                autorizacionesData.porcentaje_en_papel,
                                                            ],
                                                            backgroundColor: [
                                                                "#2D6A4F", // Verde oscuro para Cotos CYL
                                                                "#95D5B2", // Verde claro para Otros cotos
                                                            ],
                                                            borderWidth: 0,
                                                            cutout: "0%", // Sin agujero para forma de queso completo
                                                        },
                                                    ],
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: true,
                                                    plugins: {
                                                        legend: {
                                                            display: false,
                                                        },
                                                        tooltip: {
                                                            callbacks: {
                                                                label: function (
                                                                    context,
                                                                ) {
                                                                    return (
                                                                        context.label +
                                                                        ": " +
                                                                        context.parsed +
                                                                        "%"
                                                                    );
                                                                },
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                        </div>
                                        {/* Etiquetas personalizadas más grandes y centradas */}
                                        <div
                                            style={{
                                                display: "flex",
                                                // flexDirection: 'column',
                                                gap: "24px",
                                                alignItems: "center",
                                                fontSize: "13px",
                                                fontWeight: "500",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "12px",
                                                        height: "12px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                            "#2D6A4F",
                                                    }}
                                                ></div>
                                                <span
                                                    style={{ color: "#374151" }}
                                                >
                                                    CapturCYL
                                                </span>
                                                <span
                                                    style={{
                                                        color: "#374151",
                                                        fontWeight: "600",
                                                    }}
                                                >
                                                    {Math.round(
                                                        autorizacionesData.porcentaje_en_app *
                                                            10,
                                                    ) / 10}
                                                    %
                                                </span>
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "12px",
                                                        height: "12px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                            "#95D5B2",
                                                    }}
                                                ></div>
                                                <span
                                                    style={{ color: "#374151" }}
                                                >
                                                    En papel
                                                </span>
                                                <span
                                                    style={{
                                                        color: "#374151",
                                                        fontWeight: "600",
                                                    }}
                                                >
                                                    {Math.round(
                                                        autorizacionesData.porcentaje_en_papel *
                                                            10,
                                                    ) / 10}
                                                    %
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        )}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onApply={() => {
                    if (chartSettingsRef.current) {
                        chartSettingsRef.current.apply();
                    }
                }}
            >
                <ChartSettings
                    ref={chartSettingsRef}
                    chartType={chartType}
                    onChartTypeChange={setChartType}
                />
            </Modal>

            <Modal
                isOpen={isDownloadModalOpen}
                onClose={() => setIsDownloadModalOpen(false)}
                onApply={handleDownload}
                title="Descargar"
            >
                <DownloadSettings
                    fileType={fileType}
                    onFileTypeChange={setFileType}
                />
            </Modal>

            <Modal
                isOpen={isSpeciesModalOpen}
                onClose={() => setIsSpeciesModalOpen(false)}
                onApply={() => {
                    if (speciesSettingsRef.current) {
                        speciesSettingsRef.current.apply();
                        setIsSpeciesModalOpen(false);
                    }
                }}
            >
                <SpeciesSettings
                    ref={speciesSettingsRef}
                    especies={especies}
                    visibleSpecies={visibleSpecies}
                    onSpeciesChange={setVisibleSpecies}
                />
            </Modal>

            <Modal
                isOpen={isSpeciesChartModalOpen}
                onClose={() => setIsSpeciesChartModalOpen(false)}
                onApply={() => {
                    if (chartSettingsSpeciesRef.current) {
                        chartSettingsSpeciesRef.current.apply();
                    }
                }}
            >
                <ChartSettings
                    ref={chartSettingsSpeciesRef}
                    chartType={chartTypeSpecies}
                    onChartTypeChange={setChartTypeSpecies}
                />
            </Modal>

            {/* <Modal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onApply={() => {
          setIsShareModalOpen(false)
          setIsShareEmailModalOpen(true)
        }}
        title="Compartir"
      >
        <ShareSettings
          shareType={shareType}
          onShareTypeChange={setShareType}
        />
      </Modal> */}

            <Modal
                isOpen={isShareEmailModalOpen}
                onClose={() => setIsShareEmailModalOpen(false)}
                onApply={() => {
                    if (shareEmailModalRef.current) {
                        shareEmailModalRef.current.send();
                    }
                }}
                title="Compartir"
                applyLabel="Enviar"
                cancelLabel="Cancelar"
            >
                <ShareEmailModal
                    ref={shareEmailModalRef}
                    onSend={(email) => {
                        setIsShareEmailModalOpen(false);
                        // TODO: Implementar envío de archivo por email según shareType
                    }}
                    onCancel={() => setIsShareEmailModalOpen(false)}
                />
            </Modal>
        </div>
        </>
    );
}


